import React, { useState, useMemo } from 'react';
import { Table, Input, Select, Typography, Space, Tag, Tooltip, Button } from 'antd';
import { QSBase } from '../../../../../types/lab/SampleTypes';
import { SearchOutlined, DownloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { SectionCard, EmptyState } from '../../../../../components/shared';
import dayjs from 'dayjs';

const { Text } = Typography;
const { Search } = Input;

interface GeneticDataTabProps {
	qsBases: QSBase[];
}

const GeneticDataTab: React.FC<GeneticDataTabProps> = ({ qsBases }) => {
	const [searchText, setSearchText] = useState('');
	const [alleleFilter, setAlleleFilter] = useState<string>('');

	const uniquePlates = useMemo(() => {
		const plates = [...new Set(qsBases.map(item => item.PlateID).filter(Boolean))];
		return plates.sort();
	}, [qsBases]);

	const uniqueAlleles = useMemo(() => {
		const alleles = new Set<string>();
		qsBases.forEach(item => {
			if (item.A1) alleles.add(item.A1);
			if (item.A2) alleles.add(item.A2);
		});
		return Array.from(alleles).sort();
	}, [qsBases]);

	const filteredData = useMemo(() => {
		return qsBases.filter(item => {
			const matchesSearch = !searchText ||
				item.rs?.toLowerCase().includes(searchText.toLowerCase()) ||
				item.geneID?.toLowerCase().includes(searchText.toLowerCase()) ||
				item.taqmanID?.toLowerCase().includes(searchText.toLowerCase());
			const matchesAllele = !alleleFilter || item.A1 === alleleFilter || item.A2 === alleleFilter;
			return matchesSearch && matchesAllele;
		});
	}, [qsBases, searchText, alleleFilter]);

	// Allele-specific design-system colours
	const ALLELE_COLORS: Record<string, string> = {
		A: 'var(--mg-danger)',
		T: 'var(--mg-primary)',
		G: 'var(--mg-success)',
		C: 'var(--mg-warning)',
		I: 'var(--mg-info)',
		D: '#b37feb',
	};

	const getAlleleTag = (allele: string | null) => {
		if (!allele) return <Text style={{ fontSize: 12, color: 'var(--mg-text-disabled)' }}>-</Text>;
		const color = ALLELE_COLORS[allele] ?? 'var(--mg-text-secondary)';
		return (
			<Tag style={{ borderColor: `${color}40`, background: `${color}14`, color, fontSize: 12, fontFamily: 'monospace' }}>
				{allele}
			</Tag>
		);
	};

	const getGenotypeDisplay = (a1: string | null, a2: string | null) => {
		if (!a1 && !a2) return <Text style={{ fontSize: 12, color: 'var(--mg-text-disabled)' }}>--/--</Text>;
		return (
			<Space size="small">
				{getAlleleTag(a1)}
				<Text style={{ fontSize: 12 }}>/</Text>
				{getAlleleTag(a2)}
			</Space>
		);
	};

	const columns: ColumnsType<QSBase> = [
		{
			title: <Text style={{ fontSize: 12, fontWeight: 500 }}>QS Base ID</Text>,
			dataIndex: 'QS_baseID',
			key: 'QS_baseID',
			width: 120,
			render: (value) => <Text style={{ fontSize: 12, fontFamily: 'monospace' }}>{value}</Text>,
			sorter: (a, b) => a.QS_baseID - b.QS_baseID,
		},
		{
			title: <Text style={{ fontSize: 12, fontWeight: 500 }}>RS ID</Text>,
			dataIndex: 'rs',
			key: 'rs',
			width: 120,
			render: (value) => value
				? <Text style={{ fontSize: 12, fontFamily: 'monospace', color: 'var(--mg-primary)' }}>{value}</Text>
				: <Text style={{ fontSize: 12, color: 'var(--mg-text-disabled)' }}>-</Text>,
			sorter: (a, b) => (a.rs || '').localeCompare(b.rs || ''),
		},
		{
			title: <Text style={{ fontSize: 12, fontWeight: 500 }}>Genotype</Text>,
			key: 'genotype',
			width: 120,
			render: (_, record) => getGenotypeDisplay(record.A1, record.A2),
		},
		{
			title: <Text style={{ fontSize: 12, fontWeight: 500 }}>Allele 1</Text>,
			dataIndex: 'A1',
			key: 'A1',
			width: 80,
			render: (value) => getAlleleTag(value),
			filters: uniqueAlleles.map(allele => ({ text: allele, value: allele })),
			onFilter: (value, record) => record.A1 === value,
		},
		{
			title: <Text style={{ fontSize: 12, fontWeight: 500 }}>Allele 2</Text>,
			dataIndex: 'A2',
			key: 'A2',
			width: 80,
			render: (value) => getAlleleTag(value),
			filters: uniqueAlleles.map(allele => ({ text: allele, value: allele })),
			onFilter: (value, record) => record.A2 === value,
		},
		{
			title: <Text style={{ fontSize: 12, fontWeight: 500 }}>TaqMan ID</Text>,
			dataIndex: 'taqmanID',
			key: 'taqmanID',
			width: 120,
			render: (value) => value
				? <Text style={{ fontSize: 12, fontFamily: 'monospace' }}>{value}</Text>
				: <Text style={{ fontSize: 12, color: 'var(--mg-text-disabled)' }}>-</Text>,
		},
		{
			title: <Text style={{ fontSize: 12, fontWeight: 500 }}>Created</Text>,
			dataIndex: 'created_on',
			key: 'created_on',
			width: 140,
			render: (value) => <Text style={{ fontSize: 12 }}>{dayjs(value).format('MMM DD, YYYY HH:mm')}</Text>,
			sorter: (a, b) => dayjs(a.created_on).unix() - dayjs(b.created_on).unix(),
		},
	];

	const exportData = () => {
		const csvData = filteredData.map(item => ({
			QS_baseID: item.QS_baseID,
			sampleID: item.sampleID,
			PlateID: item.PlateID || '',
			geneID: item.geneID || '',
			rs: item.rs || '',
			taqmanID: item.taqmanID || '',
			A1: item.A1 || '',
			A2: item.A2 || '',
			genotype: `${item.A1 || '-'}/${item.A2 || '-'}`,
			created_on: item.created_on,
		}));
		const csvContent = [
			Object.keys(csvData[0]).join(','),
			...csvData.map(row => Object.values(row).join(','))
		].join('\n');
		const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
		const link = document.createElement('a');
		const url = URL.createObjectURL(blob);
		link.setAttribute('href', url);
		link.setAttribute('download', `genetic_data_${Date.now()}.csv`);
		link.style.visibility = 'hidden';
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	if (!qsBases || qsBases.length === 0) {
		return <EmptyState title="No genetic data" description="No genetic data available for this sample" />;
	}

	const secondaryText: React.CSSProperties = { fontSize: 12, color: 'var(--mg-text-secondary)' };

	return (
		<div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
			{/* Data Summary */}
			<SectionCard title="Genetic Data Summary">
				<div style={{ display: 'flex', gap: 0 }}>
					{[
						{ label: 'Total Records', value: qsBases.length, color: 'var(--mg-text)' },
						{ label: 'Unique Plates', value: uniquePlates.length, color: 'var(--mg-primary)' },
						{ label: 'Unique Alleles', value: uniqueAlleles.length, color: 'var(--mg-success)' },
						{ label: 'Filtered Results', value: filteredData.length, color: 'var(--mg-warning)' },
					].map(item => (
						<div key={item.label} style={{ flex: 1, textAlign: 'center' }}>
							<Text style={secondaryText}>{item.label}</Text>
							<div style={{ fontSize: 18, fontWeight: 700, color: item.color }}>{item.value}</div>
						</div>
					))}
				</div>
			</SectionCard>

			{/* Filters */}
			<SectionCard>
				<Space wrap>
					<Search
						placeholder="Search by RS ID, Gene ID, or TaqMan ID"
						allowClear
						onChange={(e) => setSearchText(e.target.value)}
						style={{ width: 300 }}
						size="small"
						prefix={<SearchOutlined />}
					/>
					<Select
						placeholder="Filter by Allele"
						allowClear
						onChange={setAlleleFilter}
						style={{ width: 180 }}
						size="small"
						options={uniqueAlleles.map(allele => ({ label: allele, value: allele }))}
					/>
					<Button icon={<DownloadOutlined />} onClick={exportData} size="small">
						Export CSV
					</Button>
				</Space>
			</SectionCard>

			{/* Genetic Data Table */}
			<SectionCard title="Genetic Variants" noPadding>
				<Table
					dataSource={filteredData}
					columns={columns}
					rowKey="QS_baseID"
					size="small"
					pagination={{
						size: 'small',
						showSizeChanger: true,
						showQuickJumper: true,
						showTotal: (total, range) => (
							<Text style={{ fontSize: 12 }}>{range[0]}-{range[1]} of {total} variants</Text>
						),
						pageSizeOptions: ['10', '25', '50', '100', '500'],
						defaultPageSize: 25,
					}}
					scroll={{ x: 'max-content', y: 400 }}
					bordered
				/>
			</SectionCard>

			{/* Allele Distribution */}
			{uniqueAlleles.length > 0 && (
				<SectionCard title="Allele Distribution">
					<div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
						{uniqueAlleles.map(allele => {
							const count = qsBases.filter(item => item.A1 === allele || item.A2 === allele).length;
							const percentage = ((count / (qsBases.length * 2)) * 100).toFixed(1);
							const color = ALLELE_COLORS[allele] ?? 'var(--mg-text-secondary)';
							return (
								<Tooltip key={allele} title={`${count} occurrences (${percentage}% of all alleles)`}>
									<Tag
										style={{ borderColor: `${color}40`, background: `${color}14`, color, fontSize: 12, cursor: 'pointer' }}
									>
										{allele}: {count}
									</Tag>
								</Tooltip>
							);
						})}
					</div>
				</SectionCard>
			)}
		</div>
	);
};

export default GeneticDataTab;
